import { useEffect, useRef, useState } from 'react';
import { BellRing, Share, SquarePlus } from 'lucide-react';
import { track } from '../onboarding/track';
import { type AskKind, type AskReason, markAsked, mayAsk } from './askPolicy';
import { usePushNotifications } from './usePushNotifications';

/**
 * After a seal: worth asking? Kept for the composer, which only needs a yes/no
 * before deciding whether to mount the invitation.
 */
export function shouldInviteToNotifications(): boolean {
  return mayAsk() != null;
}

const COPY: Record<AskReason, { title: (peer?: string | null) => string; sub: string }> = {
  sealed: {
    title: () => 'Shall we tell you when a reply arrives?',
    sub: 'Letters take their time, so there is no need to keep checking. We will send one quiet note when something reaches your letterbox, and never the letter itself.',
  },
  incoming: {
    title: (peer) =>
      peer
        ? `@${peer}'s letter is on its way. Shall we tell you when it lands?`
        : 'A letter is on its way. Shall we tell you when it lands?',
    sub: 'It arrives in its own time, so there is no need to keep checking. One quiet note when it reaches your letterbox, never the letter itself.',
  },
  email: {
    title: () => 'Hear about letters here instead of by email?',
    sub: 'Turn on notifications and we will tap you on the shoulder when a letter arrives, so you will not need those emails.',
  },
};

/**
 * Asks to tell the member when letters arrive, at a moment one is worth
 * waiting for (askPolicy.ts). On an iPhone in a browser tab it shows the Home
 * Screen steps instead: iOS only allows notifications from there.
 *
 * The browser's permission prompt only appears if the member says yes here, so
 * a "not now" costs nothing. A cold browser prompt, by contrast, is the fastest
 * way to a permanent "denied".
 */
export function NotificationInvite({
  onDone,
  reason = 'sealed',
  peerHandle = null,
  kind: givenKind,
}: {
  onDone: () => void;
  reason?: AskReason;
  peerHandle?: string | null;
  /** Decided by the caller (mayAsk); worked out here when not given. */
  kind?: AskKind;
}) {
  const { status, configLoading, busy, enable } = usePushNotifications();
  const ref = useRef<HTMLDivElement>(null);
  const [asking, setAsking] = useState(false);
  const [kind] = useState<AskKind | null>(() => givenKind ?? mayAsk({ spaced: false }));

  // Push not configured on the server, or the state changed under us: there
  // is nothing to offer, so step aside without having asked. The Home Screen
  // steps need no server, so they never step aside.
  const unavailable =
    kind == null ||
    (kind === 'notify' &&
      !configLoading &&
      (status === 'unsupported' || status === 'disabled' || status === 'subscribed'));
  useEffect(() => {
    if (unavailable) onDone();
  }, [unavailable, onDone]);

  const visible = !unavailable && (kind === 'ios-install' || !configLoading);

  // Counted once it is actually on screen, so a step-aside is not a spent ask.
  const shown = useRef(false);
  useEffect(() => {
    if (!visible || shown.current || !kind) return;
    shown.current = true;
    markAsked();
    track({ event: 'reach_prompt_shown', kind, reason });
  }, [visible, kind, reason]);

  const decline = () => {
    if (kind) track({ event: 'reach_prompt_declined', kind, reason });
    onDone();
  };

  const accept = async () => {
    if (kind === 'ios-install') {
      track({ event: 'reach_prompt_accepted', kind, reason });
      onDone();
      return;
    }
    track({ event: 'reach_prompt_accepted', kind: 'notify', reason });
    setAsking(true);
    const result = await enable();
    if (result) track({ event: 'push_permission_result', result, reason });
    onDone();
  };

  useEffect(() => {
    if (visible) ref.current?.focus();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !asking) decline();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  });

  if (!visible) return null;

  const copy = COPY[reason];

  return (
    <div className="postage-overlay" onClick={asking ? undefined : decline}>
      <div
        className="postage-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="push-invite-title"
        ref={ref}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="postage-modal__icon" aria-hidden="true">
          {kind === 'ios-install' ? <SquarePlus size={22} /> : <BellRing size={22} />}
        </div>
        {kind === 'ios-install' ? (
          <>
            <h2 id="push-invite-title" className="postage-modal__title">
              Put toiyp on your Home Screen
            </h2>
            <p className="postage-modal__sub">
              On iPhone, that is how toiyp can tell you when a letter arrives. It only takes a moment:
            </p>
            <ol className="install-steps">
              <li>
                Tap <Share size={15} aria-label="Share" className="install-steps__icon" /> at the bottom of
                Safari, or at the top right in Chrome
              </li>
              <li>
                Choose <strong>Add to Home Screen</strong>
              </li>
              <li>Open toiyp from your Home Screen, and say yes to notifications</li>
            </ol>
          </>
        ) : (
          <>
            <h2 id="push-invite-title" className="postage-modal__title">
              {copy.title(peerHandle)}
            </h2>
            <p className="postage-modal__sub">{copy.sub}</p>
            <p className="postage-modal__note">You can change this any time from the bell at the top.</p>
          </>
        )}
        <button
          type="button"
          className="btn btn--primary postage-modal__cta"
          onClick={() => void accept()}
          disabled={busy || asking}
          style={{ borderRadius: 'var(--r-full)' }}
        >
          {kind === 'ios-install' ? 'Got it' : asking ? 'Waiting for your browser…' : 'Yes, let me know'}
        </button>
        <button
          type="button"
          className="btn btn--ghost postage-modal__cta"
          onClick={decline}
          disabled={asking}
          style={{ borderRadius: 'var(--r-full)', marginTop: 0 }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
